import type { IUserForm } from '@/components/users/UserFormModal.vue'
import client from '@/services/client'
import type { IRoleAll } from '@/types/RoleTypes'
import type { IUser, IUserList } from '@/types/UserTypes'

import type { AxiosResponse } from 'axios'

const getCurrentUser = async (): Promise<AxiosResponse<IUser>> =>
  client.get<IUser>('/users/me')

const updateUserMe = async (lastName: string): Promise<AxiosResponse<IUser>> =>
  client.patch<IUser>(`/users/me`, { lastName })

export interface IUpdatePasswordPayload {
  currentPassword: string
  password: string
  password_confirmation: string
  code: string | null
  methodUuid: string | null
}
const updatePassword = async (
  payload: IUpdatePasswordPayload
): Promise<AxiosResponse<IUser>> =>
  client.patch<IUser>(`/users/password`, payload)

const getAllUsers = async (): Promise<AxiosResponse<IUserList[]>> => {
  return client.get('/users')
}

const removeUser = async (user: IUserList): Promise<AxiosResponse<void>> => {
  return client.delete(`/users/${user.uuid}`)
}

const changeStatusUser = async (
  user: IUserList,
  newStatus: boolean
): Promise<AxiosResponse<IUserList>> => {
  return client.patch(`users/${user.uuid}/active-status`, {
    isActive: newStatus,
  })
}

export interface ICreateUserPayload {
  firstName: string
  lastName: string
  email: string
  roleUuid: string
}
const createUser = async (
  userForm: IUserForm
): Promise<AxiosResponse<IUserList>> => {
  return client.post(`users`, {
    firstName: userForm.firstName,
    lastName: userForm.lastName,
    email: userForm.email,
    roleUuid: userForm.roles?.uuid || null,
  })
}

const updateUser = async (
  uuid: string,
  userForm: IUserForm
): Promise<AxiosResponse<IUserList>> => {
  return client.patch(`users/${uuid}`, {
    firstName: userForm.firstName,
    lastName: userForm.lastName,
    email: userForm.email,
    roleUuid: userForm.roles?.uuid || null,
  })
}

const updateUserRole = async (
  uuid: string,
  role: IRoleAll
): Promise<AxiosResponse<IUserList>> => {
  return client.patch(`users/${uuid}/roles`, { roleUuid: role.uuid })
}

export const userPermissions = {
  EDIT: 'users.update',
  EDIT_ROLE: 'users.updateRole',
  EDIT_STATUS: 'users.updateActiveStatus',
  DELETE_USER: 'users.delete',
  VIEW_ALL: 'users.viewAll',
  CREATE: 'users.invite',
} as const

export const UserRepository = {
  getCurrentUser,
  updateUserMe,
  updatePassword,
  getAllUsers,
  removeUser,
  changeStatusUser,
  createUser,
  updateUser,
  updateUserRole,
}
